import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { Navbar, Nav, Container } from "react-bootstrap";
import { UserMenu } from "./UserMenu";
import { useProfile } from "../../hooks/useProfile";
import { usePermissions } from "../../hooks/usePermissions";
import { LoginButton } from "../LoginButton";

export const Navigation: React.FC = () => {
  const location = useLocation();
  const profile = useProfile();
  const { hasPermission } = usePermissions();
  const isAuthenticated = !!profile;
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  const closeNav = () => setIsNavExpanded(false);

  // Close navbar when clicking outside
  useEffect(() => {
    if (!isNavExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsNavExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNavExpanded]);

  return (
    <Navbar
      ref={navRef}
      expand="lg"
      className="shadow-sm"
      expanded={isNavExpanded}
      onToggle={setIsNavExpanded}
    >
      <Container>
        <Navbar.Brand
          as={Link}
          to="/"
          className="d-flex align-items-center me-2"
          onClick={closeNav}
        >
          <img
            src="/florida-health-logo-square-flt-big-sun.svg"
            alt="Florida Health"
            width="32"
            className="me-2"
            onError={(e) => {
              // Hide image if it doesn't exist
              e.currentTarget.style.display = "none";
            }}
          />
          FLEHS
        </Navbar.Brand>

        {/* Toggle when collapsed, login button when not authenticated */}
        {isAuthenticated ? (
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
        ) : (
          <span className="d-lg-none">
            <LoginButton
              loginRequest={{
                scopes: ["https://graph.microsoft.com/User.Read"],
              }}
            />
          </span>
        )}

        <Navbar.Collapse id="basic-navbar-nav">
          {isAuthenticated && (
            <>
              {/* User menu at top when collapsed */}
              <Nav className="d-lg-none">
                <UserMenu
                  user={profile}
                  collapsed
                  onToggle={setIsUserMenuOpen}
                />
              </Nav>

              <Nav
                className={`me-auto${
                  isUserMenuOpen ? " d-none d-lg-flex" : ""
                }`}
              >
                {hasPermission("Mosquito.Read") && (
                  <Nav.Link
                    as={Link}
                    to="/mosquito"
                    onClick={closeNav}
                    className={
                      location.pathname.startsWith("/mosquito") ? "active" : ""
                    }
                  >
                    Mosquito Pools
                  </Nav.Link>
                )}
                {hasPermission("Sentinel.Read") && (
                  <Nav.Link
                    as={Link}
                    to="/sentinel"
                    onClick={closeNav}
                    className={
                      location.pathname.startsWith("/sentinel") ? "active" : ""
                    }
                  >
                    Sentinel Flocks
                  </Nav.Link>
                )}
                {hasPermission("Veterinary.Read") && (
                  <Nav.Link
                    as={Link}
                    to="/veterinary"
                    onClick={closeNav}
                    className={
                      location.pathname.startsWith("/veterinary")
                        ? "active"
                        : ""
                    }
                  >
                    Veterinary Cases
                  </Nav.Link>
                )}
                {/* {hasPermission("Users.Read") && (
                  <Nav.Link
                    as={Link}
                    to="/admin"
                    onClick={closeNav}
                    className={
                      location.pathname.startsWith("/admin") ? "active" : ""
                    }
                  >
                    Admin
                  </Nav.Link>
                )} */}
              </Nav>
            </>
          )}

          {/* User menu at end when expanded, login button when not authenticated */}
          <Nav className="ms-auto d-none d-lg-flex align-items-center">
            {isAuthenticated ? (
              <UserMenu user={profile} />
            ) : (
              <LoginButton
                loginRequest={{
                  scopes: ["https://graph.microsoft.com/User.Read"],
                }}
              />
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};
